// ─── Le modifiche a una lezione, finché non si confermano (voce 17) ─────────
//
// 🗣️ 23/09/2026, dalla scheda della lezione: *«quando vario o il giorno, o la
// durata, o il numero di campo, o modifico una nota, dovrei avere un bottone
// da spingere che mi fa confermare»*. Mockup approvato:
// `mockup/scheda-lezione-maestro-mockup.html`.
//
// ⭐⭐ **FINCHÉ STA QUI NON È SUCCESSO NIENTE.** Toccare la durata, il campo,
// l'ora o la nota scrive in questa bozza e basta: la lezione vera resta
// com'era, e nessun allievo riceve niente. Parte tutto insieme da «Conferma le
// modifiche» — un giro solo, un avviso solo.
// 📌 *Un gesto che avvisa delle persone non si fa a rate: tre tocchi di fila
//    sarebbero tre messaggi sullo stesso telefono.*
//
// ⚖️ **Sta FUORI dalla schermata, in memoria, e non nello stato di React.**
// Scegliere l'ora nuova è un'altra schermata (`/sposta`), e uno stato della
// scheda morirebbe andando di là: al ritorno la durata cambiata un attimo
// prima sarebbe sparita. Qui sopravvive al viaggio, e muore da sé
// ricaricando la pagina — che è il modo in cui un telefono dice «ricomincio».
//
// ⚖️ E le regole sono **pure**, così una prova le sabota senza un browser
// (⇒ `bozza.test.ts`).

import { useSyncExternalStore } from 'react';
import type { MiaLezione } from '@pmo/shared';

/**
 * Cosa è cambiato. Una chiave c'è **solo** se il valore è diverso da quello
 * della lezione: una modifica rimessa com'era sparisce, non resta a contare.
 */
export interface Modifiche {
  date?: string;
  time?: string;
  durationMinutes?: number;
  /** Il campo viaggia col suo nome, o la riga «Dove» dovrebbe andarlo a cercare. */
  campo?: { courtId: string; courtName: string };
  /** `null` = togliere la nota. */
  nota?: string | null;
}

/** Com'è la lezione **se si confermasse adesso**: quella vera, con sopra la bozza. */
export interface Vista {
  date: string;
  time: string;
  durationMinutes: number;
  courtId: string;
  courtName: string;
  nota: string | null;
}

type Lezione = Pick<MiaLezione, 'date' | 'time' | 'durationMinutes' | 'courtId' | 'courtName' | 'nota'>;

export function vista(l: Lezione, m: Modifiche): Vista {
  return {
    date: m.date ?? l.date,
    time: m.time ?? l.time,
    durationMinutes: m.durationMinutes ?? l.durationMinutes,
    courtId: m.campo?.courtId ?? l.courtId,
    courtName: m.campo?.courtName ?? l.courtName,
    nota: m.nota !== undefined ? m.nota : l.nota,
  };
}

/** 📝 Una nota vuota è **nessuna** nota: si toglie, non si scrive una stringa vuota. */
export function notaPulita(testo: string | null): string | null {
  const t = (testo ?? '').trim();
  return t === '' ? null : t;
}

/**
 * ⭐ La bozza con dentro un cambio in più, **e senza le chiavi rimesse com'erano**.
 *
 * 🚨 Si confronta con la lezione **vera**, non con la bozza di prima: cambiare
 * la durata da 1 ora a 1 ora e mezza e poi di nuovo a 1 ora non sono due
 * modifiche, è nessuna — e la barra in fondo non deve chiedere di confermare
 * una cosa che non cambia niente.
 * ⚠️ È anche la ragione per cui la stessa funzione serve a rileggere una
 *    bozza vecchia (⇒ `pulisci`): se nel frattempo la lezione vera è diventata
 *    come la bozza, quella chiave non è più una modifica.
 */
export function conCambio(l: Lezione, m: Modifiche, cambio: Modifiche): Modifiche {
  const tutto = { ...m, ...cambio };
  const fuori: Modifiche = {};
  if (tutto.date !== undefined && tutto.date !== l.date) fuori.date = tutto.date;
  if (tutto.time !== undefined && tutto.time !== l.time) fuori.time = tutto.time;
  if (tutto.durationMinutes !== undefined && tutto.durationMinutes !== l.durationMinutes)
    fuori.durationMinutes = tutto.durationMinutes;
  if (tutto.campo !== undefined && tutto.campo.courtId !== l.courtId) fuori.campo = tutto.campo;
  if (tutto.nota !== undefined) {
    const n = notaPulita(tutto.nota);
    if (n !== notaPulita(l.nota)) fuori.nota = n;
  }
  return fuori;
}

export function pulisci(l: Lezione, m: Modifiche): Modifiche {
  return conCambio(l, m, {});
}

/** Quante cose cambiano: è il numero che la barra scrive («2 modifiche»). */
export function quante(m: Modifiche): number {
  return Object.keys(m).length;
}

/** Cambia dove o quando si gioca: è questo, e solo questo, che fa partire un avviso. */
export function cambiaLaCollocazione(m: Modifiche): boolean {
  return (
    m.date !== undefined || m.time !== undefined || m.durationMinutes !== undefined || m.campo !== undefined
  );
}

/**
 * 🔔 **Chi riceverebbe l'avviso, contato come lo conta il server.**
 *
 * ⚖️ Il server avvisa i **soci** in campo (⇒ `avvisi/coda.ts`, `sociInCampo`):
 * un ospite non ha un telefono nel gestionale, quindi non riceve niente. Anche
 * chi è segnato assente è un socio, e l'avviso lo riceve.
 * 📌 *Un numero che promette degli avvisi deve contare le stesse persone di
 *    chi li manda, o la barra promette messaggi che non partono.*
 */
export function quantiAvvisati(l: Pick<MiaLezione, 'players'>, m: Modifiche): number {
  if (!cambiaLaCollocazione(m)) return 0;
  return l.players.filter((g) => !g.ospite).length;
}

// ── La memoria, una bozza per lezione ────────────────────────────────────────

const bozze = new Map<string, Modifiche>();
const chiAscolta = new Set<() => void>();
const VUOTA: Modifiche = Object.freeze({}) as Modifiche;

function avvisaChiAscolta(): void {
  for (const f of chiAscolta) f();
}

export function leggiBozza(id: string): Modifiche {
  return bozze.get(id) ?? VUOTA;
}

export function scriviBozza(id: string, m: Modifiche): void {
  if (quante(m) === 0) bozze.delete(id);
  else bozze.set(id, m);
  avvisaChiAscolta();
}

export function buttaBozza(id: string): void {
  if (bozze.delete(id)) avvisaChiAscolta();
}

/** La bozza di una lezione, che si ridisegna quando cambia — anche da un'altra schermata. */
export function useBozza(id: string): Modifiche {
  return useSyncExternalStore(
    (f) => {
      chiAscolta.add(f);
      return () => chiAscolta.delete(f);
    },
    () => leggiBozza(id),
  );
}
